"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.portalHiddenSectionsSchema = exports.PORTAL_LAST_TAB_MESSAGE = exports.PORTAL_SECTIONS = exports.PORTAL_HEADER_SECTIONS = exports.PORTAL_TAB_SECTIONS = void 0;
exports.isPortalSection = isPortalSection;
exports.isPortalTabSection = isPortalTabSection;
exports.hidesEveryPortalTab = hidesEveryPortalTab;
exports.parsePortalHiddenSections = parsePortalHiddenSections;
exports.isPortalSectionHidden = isPortalSectionHidden;
exports.visiblePortalTabs = visiblePortalTabs;
exports.portalLandingTab = portalLandingTab;
exports.portalTabOrLanding = portalTabOrLanding;
exports.isLastVisiblePortalTab = isLastVisiblePortalTab;
exports.withPortalSectionHidden = withPortalSectionHidden;
const zod_1 = require("zod");
/**
 * Portal sections a venue can hide (owner, 2026-09-28: "i want to be able to
 * hide sections on the portal, for example home, or Session or Games or
 * Challenges or Prize Vault").
 *
 * ONE VOCABULARY FOR THREE READERS. The staff Settings -> Portal page lists
 * these, the API stores and refuses by them, and the kiosk shell hides by them.
 * A second copy of the list in any one of the three is how a section ends up
 * gone from the nav and still one Home tile away.
 *
 * Stored as `VenueSettings.portalHiddenSections` (TEXT[]): the list of what is
 * HIDDEN, never of what is shown, so the empty default is exactly the portal as
 * it was before this existed, and a section added later appears on every venue
 * without a migration having to touch their rows.
 *
 * Deliberately NOT a section:
 *  - the lock screen before sign-in, which has its own switches (account
 *    creation, the wi-fi QR);
 *  - the PausedOverlay's own call-staff button. A pause is the venue asking the
 *    customer to come and talk; hiding the way to do that would strand them
 *    behind a frozen clock. `assist` hides the HEADER button only.
 */
/**
 * The signed-in shell's six tabs, in nav order — the portal's own area ids
 * (`PORTAL_TABS` in portal-web's SessionPage), so a hidden id and a tab can be
 * compared without a mapping. `arcade` is the tab customers read as
 * Challenges.
 *
 * The order is load-bearing: the landing tab is the FIRST visible one
 * ({@link portalLandingTab}), which is Home until Home is hidden.
 */
exports.PORTAL_TAB_SECTIONS = ['home', 'session', 'games', 'apps', 'arcade', 'vault'];
/**
 * Header buttons that are not tabs.
 *
 * - `shop` — the Shop button AND every other way into the shop layer (Home's
 *   shop tile among them). Independent of `enablePortalOrders`, which still
 *   decides whether an order can be placed at all: this is about the screen,
 *   that one is about the kitchen.
 * - `assist` — the header's call-staff button (`AssistButton
 *   placement="header"`).
 *
 * Both may be hidden together; neither is a way to navigate, so hiding them
 * cannot leave the shell with nowhere to stand.
 */
exports.PORTAL_HEADER_SECTIONS = ['shop', 'assist'];
/** Every hideable section, in the one canonical order stored lists keep. */
exports.PORTAL_SECTIONS = [...exports.PORTAL_TAB_SECTIONS, ...exports.PORTAL_HEADER_SECTIONS];
/**
 * The refusal for a list that hides every tab. The API answers it against the
 * `portalHiddenSections` field so the staff page can place it; the page itself
 * never gets there, because it disables the last visible tab's switch.
 */
exports.PORTAL_LAST_TAB_MESSAGE = 'Keep at least one portal tab visible.';
function isPortalSection(value) {
    return typeof value === 'string' && exports.PORTAL_SECTIONS.includes(value);
}
function isPortalTabSection(value) {
    return typeof value === 'string' && exports.PORTAL_TAB_SECTIONS.includes(value);
}
/**
 * Whether a RAW list hides all six tabs. The write-side rule, so it reads the
 * list as sent — {@link parsePortalHiddenSections} would already have repaired
 * it.
 */
function hidesEveryPortalTab(hidden) {
    if (!hidden)
        return false;
    return exports.PORTAL_TAB_SECTIONS.every((tab) => hidden.includes(tab));
}
/**
 * A stored or received value -> the canonical hidden list: known ids only,
 * each once, in {@link PORTAL_SECTIONS} order.
 *
 * UNKNOWN IDS ARE DROPPED, NOT REFUSED. The column outlives any one build: a
 * row written by a later build that knows a section this one does not must
 * still read, and a section retired later must not wedge the Portal page of
 * every venue that once hid it. Dropping is also what makes the read
 * idempotent, so "save what you were shown" never changes anything.
 *
 * A LIST THAT HIDES EVERY TAB READS WITH THE FIRST TAB SHOWN. The API refuses
 * to write one, so only a hand edit of the row can produce it — and a kiosk
 * with no tab at all is a blank screen in front of a paying customer. The
 * staff page reads the same repaired list, so what it shows is what the PCs
 * show.
 */
function parsePortalHiddenSections(value) {
    if (!Array.isArray(value))
        return [];
    const hidden = new Set(value.filter(isPortalSection));
    const canonical = exports.PORTAL_SECTIONS.filter((section) => hidden.has(section));
    if (!hidesEveryPortalTab(canonical))
        return canonical;
    const [first] = exports.PORTAL_TAB_SECTIONS;
    return canonical.filter((section) => section !== first);
}
/**
 * The hidden list on the Settings update body (field `portalHiddenSections`).
 *
 * Unknown ids ARE refused here, unlike on read: the staff page only ever sends
 * ids it was given, so an unknown one is a typo or a stale client, and
 * dropping it silently would report a save that did not do what was asked.
 * Duplicates and order are not refused — they mean nothing — and come out
 * canonical. The bound is generous on purpose (duplicates are legal); it only
 * stops a body from being a denial of service.
 *
 * NO `.default([])`. A PATCH that omits the field must leave the stored list
 * alone; a default would un-hide every section whenever another page saves.
 */
exports.portalHiddenSectionsSchema = zod_1.z
    .array(zod_1.z.enum(exports.PORTAL_SECTIONS))
    .max(32)
    .refine((hidden) => !hidesEveryPortalTab(hidden), { message: exports.PORTAL_LAST_TAB_MESSAGE })
    .transform((hidden) => parsePortalHiddenSections(hidden));
/* -------------------------------------------------------------------------- */
/* What the screens ask                                                       */
/* -------------------------------------------------------------------------- */
function isPortalSectionHidden(hidden, section) {
    return parsePortalHiddenSections(hidden).includes(section);
}
/** The tabs the nav shows, in nav order. Never empty. */
function visiblePortalTabs(hidden) {
    const parsed = parsePortalHiddenSections(hidden);
    return exports.PORTAL_TAB_SECTIONS.filter((tab) => !parsed.includes(tab));
}
/**
 * Where a sign-in lands: the first visible tab. Home until Home is hidden —
 * a venue that hides Home has said it does not want customers starting there.
 */
function portalLandingTab(hidden) {
    return visiblePortalTabs(hidden)[0] ?? exports.PORTAL_TAB_SECTIONS[0];
}
/**
 * `tab` while it is visible, else the landing tab. For the shell when a poll
 * brings a list that hides the tab the customer is standing on, and for any
 * deep link or shortcut that names a tab: no path may stop on a hidden one.
 */
function portalTabOrLanding(hidden, tab) {
    return visiblePortalTabs(hidden).includes(tab) ? tab : portalLandingTab(hidden);
}
/**
 * Whether `section` is the one tab still showing — the staff page disables
 * its switch rather than let the owner reach the API's refusal.
 */
function isLastVisiblePortalTab(hidden, section) {
    if (!isPortalTabSection(section))
        return false;
    const visible = visiblePortalTabs(hidden);
    return visible.length === 1 && visible[0] === section;
}
/**
 * The canonical list with `section` hidden or shown. Hiding the last visible
 * tab returns the list unchanged — the same rule the API enforces, applied
 * where the switch is flipped.
 */
function withPortalSectionHidden(hidden, section, hide) {
    const current = parsePortalHiddenSections(hidden);
    if (hide && isLastVisiblePortalTab(current, section))
        return current;
    const next = hide ? [...current, section] : current.filter((entry) => entry !== section);
    return parsePortalHiddenSections(next);
}
//# sourceMappingURL=portal-sections.js.map